import { useMemo, useState } from "react";
import { Button, Chip, FormFieldSet } from "../../../../../../components";

export const Badge = ({ cat, categories }) => {
  const { label } = categories[cat] || {};

  const catColorMap = {
    core:       "info",
    display:    "success",
    behavior:   "warning",
    suggestion: "primary",
    validation: "danger",
    generation: "invoiced",
    range:      "leave",
    limit:      "draft",
    search:     "primary",
    default:    "warning",
    server:     "danger",
    filter:     "leave",
    layout:     "draft",
    event:      "warning",
    data:       "info",
    ui:         "success",
  };

  return (
    <Chip
      label={label}
      variant="contained"
      size="xs-sm"
      color={catColorMap[cat] || "info"}
    />
  );
};
export const PropsTable = ({ data, title, typeLabel, categories }) => {
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const filtered = useMemo(() => {
    const q = search?.toLowerCase()?.trim();
    return data?.filter((p) => {
      const matchCat = activeFilter === "all" || p?.cat === activeFilter;
      const matchQ =
        !q ||
        p?.prop?.toLowerCase()?.includes(q) ||
        p?.desc?.toLowerCase()?.includes(q);
      return matchCat && matchQ;
    });
  }, [search, activeFilter, data]);

  const pills = [
    { key: "all", label: "All" },
    ...Object.entries(categories)?.map(([k, v]) => ({
      key: k,
      label: v?.label,
    })),
  ];

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
        <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">
          {typeLabel}
        </code>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <FormFieldSet
          noTitle
          placeholder="Search props…"
          size="sm"
          value={search}
          onChange={(val) => setSearch(val)}
        />
        <div className="flex flex-wrap gap-2">
          {pills?.map(({ key, label }) => (
            <Button
              key={key}
              size="sm"
              onClick={() => setActiveFilter(key)}
              variant="outlined"
              title={label}
              className={`text-xs !px-3 !py-1 rounded-full border transition-colors ${
                activeFilter === key
                  ? "!bg-info-50 !text-info-700 !border-info-300"
                  : "!bg-gray-50 !text-gray-500 !border-gray-200 hover:!bg-gray-100"
              }`}
            />
          ))}
        </div>
        <div className="text-xs text-gray-400 ml-auto">
          {filtered?.length} / {data?.length} props
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs whitespace-nowrap">
                Prop
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs whitespace-nowrap">
                Category
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs whitespace-nowrap">
                Default
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs whitespace-nowrap">
                Type
              </th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-500 text-xs">
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered?.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-10 text-gray-400">
                  No props match your search.
                </td>
              </tr>
            ) : (
              filtered?.map((p) => (
                <tr
                  key={p?.prop}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <code className="text-xs font-semibold text-gray-800 bg-gray-100 px-1.5 py-0.5 rounded">
                      {p?.prop}
                    </code>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <Badge cat={p?.cat} categories={categories} />
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    <code className="text-xs text-gray-500 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">
                      {p?.def}
                    </code>
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-gray-500 whitespace-nowrap">
                    {p?.type}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-gray-600 leading-relaxed">
                    {p?.desc}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const TAGLIST_PROPS = [
  // Core
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: "tags",
    used: true,
    desc: 'Must be set to "tags" to render the tags input field.',
  },
  {
    prop: "value",
    cat: "core",
    def: "[]",
    type: "string[]",
    used: true,
    desc: "Controlled array of currently added tags. Pass your state variable here.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(tags: string[]) => void",
    used: true,
    desc: "Fires with the updated tags array whenever a tag is added or removed. Pass your state setter directly.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    used: false,
    desc: "Label shown above the input field.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    used: false,
    desc: "Set to true to hide the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Placeholder"',
    type: "string",
    used: false,
    desc: "Placeholder text shown inside the input when no tags are added yet.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"sm" | "md" | "lg"',
    used: false,
    desc: "Controls the padding and font size of the input field.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    used: false,
    desc: "Controls the border radius of the input field.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    used: false,
    desc: "Adds a subtle box shadow to the input instead of a visible border.",
  },
  {
    prop: "error",
    cat: "validation",
    def: "false",
    type: "string | false",
    used: false,
    desc: "Error message shown in red below the field. Pass a string to show the error.",
  },
  {
    prop: "required",
    cat: "validation",
    def: "false",
    type: "boolean",
    used: false,
    desc: "Shows a red asterisk (*) next to the label to mark the field as required.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    used: false,
    desc: "Disables the entire input. No tags can be added or removed.",
  },
  {
    prop: "maxCount",
    cat: "behavior",
    def: "10",
    type: "number",
    used: true,
    desc: "Maximum number of tags allowed. Input is disabled once this limit is reached.",
  },
  {
    prop: "validate",
    cat: "validation",
    def: "null",
    type: "(tag: string) => true | string",
    used: false,
    desc: 'Custom validation function. Return true if valid, or an error message string if invalid. Example: (tag) => tag.length >= 2 || "Min 2 chars"',
  },
  {
    prop: "allowedPattern",
    cat: "validation",
    def: "null",
    type: "RegExp",
    used: false,
    desc: "RegExp to restrict characters while typing. Only characters matching the pattern are allowed. Example: /^[a-zA-Z\\s]*$/ allows only letters and spaces.",
  },
  {
    prop: "suggestionsList",
    cat: "suggestion",
    def: "[]",
    type: "string[]",
    used: true,
    desc: "List of suggestions shown in a dropdown as the user types. Already-added tags are excluded from suggestions automatically.",
  },
];
export const RADIOLIST_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: "radio",
    desc: 'Must be "radio" to render the radio button group.',
  },
  {
    prop: "options",
    cat: "core",
    def: "[...]",
    type: "{ label: string, value: any }[]",
    desc: "Array of options to render as radio buttons. Each item needs a label (displayed text) and a value.",
  },
  {
    prop: "value",
    cat: "core",
    def: '""',
    type: "string | any",
    desc: "Currently selected value. Controlled — matched against each option's value to determine which radio is checked.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(e: ChangeEvent) => void",
    desc: "Fires when a radio is selected. Receives the native change event — use e.target.value to read the selected value.",
  },
  {
    prop: "name",
    cat: "behavior",
    def: '"custom-radio"',
    type: "string",
    desc: "HTML name attribute shared across all radio buttons in this group. Required when multiple radio groups exist on the same page to keep selections independent.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the radio group.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"sm" | "md" | "lg" | "xl"',
    desc: "Controls the size of the radio circle and label text.",
  },
  {
    prop: "color",
    cat: "display",
    def: '"primary"',
    type: '"primary" | "warning" | "success" | "danger" | "black" | "white"',
    desc: "Filled color of the radio circle when selected. Also controls the border color on hover.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables all options. Shows muted gray style with reduced opacity. No selection can be made.",
  },
  {
    prop: "list",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Stacks options vertically (flex-col gap-2) instead of the default horizontal wrap.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const CHECKBOXLIST_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: "checkbox",
    desc: 'Must be "checkbox" to render the checkbox group.',
  },
  {
    prop: "options",
    cat: "core",
    def: "[...]",
    type: "{ label: string, value: any }[]",
    desc: "Array of options to render as checkboxes. Each needs a label (displayed text) and a value.",
  },
  {
    prop: "values",
    cat: "core",
    def: "[]",
    type: "any[]",
    desc: "Controlled array of currently checked values. Note: use values (plural) — not value — for checkboxes.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(selectedValues: any[]) => void",
    desc: "Fires with the updated array whenever a checkbox is toggled. Receives the new array directly — not a native event.",
  },
  {
    prop: "multiple",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Allows multiple items to be checked at once. When false, selecting one deselects all others.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the checkbox group.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"sm" | "md" | "lg" | "xl"',
    desc: "Controls the size of the checkbox squares and label text.",
  },
  {
    prop: "color",
    cat: "display",
    def: '"primary"',
    type: '"primary" | "warning" | "success" | "danger" | "black" | "white"',
    desc: "Fill color of the checkbox square when checked. Also controls border color on hover.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables all checkboxes. No selection or deselection can be made.",
  },
  {
    prop: "list",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Stacks options vertically (flex-col gap-2) instead of the default horizontal wrap.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const INPUTLIST_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: "text",
    desc: 'Must be "text" to render the text input.',
  },
  {
    prop: "value",
    cat: "core",
    def: '""',
    type: "string | number",
    desc: "Controlled value of the input. Always pass your state variable here.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(value: string) => void",
    desc: "Fires with the new value string on every keystroke. Note: receives the value directly — not a native event.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the input field.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Placeholder"',
    type: "string",
    desc: "Placeholder text shown inside the input when empty.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the input.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius of the input.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the input. Shows a gray background with cursor-not-allowed.",
  },
  {
    prop: "onKeyDown",
    cat: "behavior",
    def: "undefined",
    type: "(e: KeyboardEvent) => void",
    desc: "Fires on keydown inside the input. Useful for handling Enter, Tab, or shortcut keys.",
  },
  {
    prop: "onFocus",
    cat: "behavior",
    def: "undefined",
    type: "(e: FocusEvent) => void",
    desc: "Fires when the input receives focus.",
  },
  {
    prop: "onPaste",
    cat: "behavior",
    def: "undefined",
    type: "(e: ClipboardEvent) => void",
    desc: "Fires when the user pastes content into the input.",
  },
  {
    prop: "suggestionsList",
    cat: "suggestion",
    def: "[]",
    type: "string[] | { label, value }[]",
    desc: "List of suggestions shown in a dropdown as the user types. Filters automatically based on the current input value.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const SELECT_PROPS = [
  // type="select"
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"select"',
    desc: 'Must be "select" to render the standard dropdown.',
  },
  {
    prop: "options",
    cat: "core",
    def: "[]",
    type: "{ label, value, badges?, isDisabled? }[]",
    desc: "Array of options. Each needs label and value. Optionally add isDisabled to grey out an option, or badges for badge indicators.",
  },
  {
    prop: "value",
    cat: "core",
    def: '""',
    type: "string | object | array",
    desc: "Controlled selected value. Pass a single value string for single select, or array of values for multiple.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(value) => void",
    desc: "Fires with the selected value (string) on single select, or array of values on multiple select.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the dropdown.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Select an option"',
    type: "string",
    desc: "Text shown when no option is selected.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "xs-sm" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the dropdown trigger button.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius of the dropdown trigger.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the dropdown. Shows gray background. Cannot be opened.",
  },
  {
    prop: "multiple",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Enables multi-selection. onChange receives an array of values. Selected items shown as chips or badges in the trigger.",
  },
  {
    prop: "style",
    cat: "display",
    def: '"normal"',
    type: '"normal" | "chip"',
    desc: 'Controls how selected items are displayed in the trigger when multiple is true. "chip" shows Chip components, "normal" shows badge-style spans.',
  },
  {
    prop: "showSearch",
    cat: "search",
    def: "false",
    type: "boolean",
    desc: "Shows a search input at the top of the dropdown to filter options.",
  },
  {
    prop: "showButton",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: 'Shows an "Add New" button inside the dropdown. Opens an AddOptionPopup to create a new option on the fly.',
  },
  {
    prop: "alertName",
    cat: "behavior",
    def: "undefined",
    type: "string",
    desc: "Passed to AddOptionPopup as the entity name shown when adding a new option.",
  },
  {
    prop: "icon",
    cat: "display",
    def: "false",
    type: "string | boolean",
    desc: 'Icon shown on the "Add New" button inside the dropdown.',
  },
  {
    prop: "showClearButton",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Shows an × button inside the trigger to clear the current selection.",
  },
  {
    prop: "showBadges",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Enables badge indicators next to each option in the dropdown. Requires options to have a badges object.",
  },
  {
    prop: "badgeSize",
    cat: "display",
    def: '"xs"',
    type: "string",
    desc: "Size of the InfoCircle badge components shown next to options when showBadges is true.",
  },
  {
    prop: "onClose",
    cat: "behavior",
    def: "undefined",
    type: "() => void",
    desc: "Fires when the dropdown closes. Useful for resetting server-side search.",
  },
  {
    prop: "searchValue",
    cat: "search",
    def: '""',
    type: "string",
    desc: "Controlled search value for server-side search. Use with onSearch.",
  },
  {
    prop: "onSearch",
    cat: "search",
    def: "undefined",
    type: "(value: string) => void",
    desc: "Server-side search handler. When provided, typing in the search box calls this instead of filtering locally.",
  },
  {
    prop: "refData",
    cat: "behavior",
    def: "undefined",
    type: "React.Ref",
    desc: "Ref attached to the options list container. Useful for infinite scroll implementations.",
  },
  {
    prop: "handleScroll",
    cat: "behavior",
    def: "undefined",
    type: "(e: ScrollEvent) => void",
    desc: "Fires when the options list is scrolled. Use with refData for infinite scroll.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const RADIO_SELECT_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"radioSelect"',
    desc: 'Must be "radioSelect" to render the radio-style select dropdown.',
  },
  {
    prop: "options",
    cat: "core",
    def: "[]",
    type: "{ label, value }[]",
    desc: "Array of options shown in the dropdown as radio buttons.",
  },
  {
    prop: "value",
    cat: "core",
    def: "undefined",
    type: "string | any",
    desc: "Controlled selected value. Matched against each option's value.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(value) => void",
    desc: "Fires with the selected value when the user picks an option.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the dropdown.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Select an option"',
    type: "string",
    desc: "Text shown in the trigger when nothing is selected.",
  },
  {
    prop: "showSearch",
    cat: "search",
    def: "false",
    type: "boolean",
    desc: "Shows a search input inside the dropdown to filter options.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the dropdown entirely.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const MULTI_SELECT_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"multiSelect"',
    desc: 'Must be "multiSelect" to render the checkbox-based multi-select dropdown.',
  },
  {
    prop: "options",
    cat: "core",
    def: "[]",
    type: "{ label, value }[]",
    desc: "Array of options shown as checkboxes inside the dropdown.",
  },
  {
    prop: "value",
    cat: "core",
    def: "[]",
    type: "any[]",
    desc: "Controlled array of currently selected values.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(values: any[]) => void",
    desc: "Fires with the updated array of selected values when any checkbox is toggled.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the dropdown.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Select an option"',
    type: "string",
    desc: "Text shown in the trigger when nothing is selected.",
  },
  {
    prop: "showSearch",
    cat: "search",
    def: "false",
    type: "boolean",
    desc: "Shows a search input at the top of the dropdown to filter options.",
  },
  {
    prop: "showSelectAll",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: 'Shows a "Select All" checkbox at the top to select or deselect all options at once.',
  },
  {
    prop: "showViewButton",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: 'Shows a "View" button next to each option. Fires onViewClick when clicked.',
  },
  {
    prop: "onViewClick",
    cat: "behavior",
    def: "undefined",
    type: "(value) => void",
    desc: "Fires when the View button next to an option is clicked. Receives the option value.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the dropdown entirely.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const SELECT_DEFAULT_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"selectDefault"',
    desc: 'Must be "selectDefault" to render the select with default-setting capability.',
  },
  {
    prop: "options",
    cat: "core",
    def: "[]",
    type: "{ label, value, logo? }[]",
    desc: "Array of options. Each needs label and value. Optionally add a logo URL to show company logos.",
  },
  {
    prop: "value",
    cat: "core",
    def: "undefined",
    type: "string",
    desc: "Controlled selected value.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(value: string) => void",
    desc: "Fires with the selected value when the user picks an option.",
  },
  {
    prop: "defaultValue",
    cat: "default",
    def: "null",
    type: "string",
    desc: "Value of the option that is marked as default. That option is pinned to the top and shows a 'Default' badge.",
  },
  {
    prop: "onSetDefault",
    cat: "default",
    def: "undefined",
    type: "(value: string) => void",
    desc: "Fires when the user clicks 'Set as Default' from the option's context menu. Update your defaultValue state here.",
  },
  {
    prop: "showLogo",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a circular logo image next to each option. Requires options to have a logo URL. Falls back to the first letter of the label if the image fails to load.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the dropdown.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Select an option"',
    type: "string",
    desc: "Text shown in the trigger when nothing is selected.",
  },
  {
    prop: "showSearch",
    cat: "search",
    def: "false",
    type: "boolean",
    desc: "Shows a search input at the top of the dropdown.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the dropdown entirely.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const DATEPICKER_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"datepicker" | "datepickerRange"',
    desc: 'Use "datepicker" for single date selection. Use "datepickerRange" for selecting a from–to date range.',
  },
  {
    prop: "value",
    cat: "core",
    def: "undefined",
    type: "Date | string | { from, to }",
    desc: 'Single date: pass a Date object or ISO string. Range: pass { from: Date, to: Date }. Accepts "from"/"start" and "to"/"end" key aliases.',
  },
  {
    prop: "onChange",
    cat: "core",
    def: "() => {}",
    type: "(value) => void",
    desc: "Single date: fires with an ISO datetime string. Range: fires with { from: ISOString, to: ISOString }. Fires null when cleared.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the date picker.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Select a date"',
    type: "string",
    desc: "Text shown in the input when no date is selected.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the input trigger.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius of the input trigger.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the input. The calendar cannot be opened and no date can be selected.",
  },
  {
    prop: "showTime",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Shows hour, minute, and AM/PM selectors inside the calendar popup. Only works with single date mode.",
  },
  {
    prop: "showDropdown",
    cat: "behavior",
    def: "true",
    type: "boolean",
    desc: "When true, clicking the input shows a preset options list (Today, Yesterday, This Week, etc.) before opening the calendar. Set to false to open the calendar directly.",
  },
  {
    prop: "defaultOption",
    cat: "behavior",
    def: "undefined",
    type: "string | number",
    desc: 'Pre-selects a preset option on mount. Pass the option label as a string (e.g. "today", "last month") or its numeric value. Only applies when no value is already set.',
  },
  {
    prop: "minDate",
    cat: "limit",
    def: 'new Date("1900-01-01")',
    type: "Date",
    desc: "Earliest selectable date. Dates before this are greyed out and unclickable in the calendar.",
  },
  {
    prop: "maxDate",
    cat: "limit",
    def: "undefined",
    type: "Date",
    desc: "Latest selectable date. Dates after this are greyed out and unclickable in the calendar.",
  },
  {
    prop: "blockDates",
    cat: "limit",
    def: "[]",
    type: "(Date | string | { from, to })[]",
    desc: "Array of individual dates or date ranges to block. Blocked dates show a strikethrough and cannot be selected. Accepts Date objects, ISO strings, or { from, to } range objects.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const MONTHPICKER_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"monthpicker" | "monthpickerRange"',
    desc: 'Use "monthpicker" for single month selection. Use "monthpickerRange" for selecting a from–to month range.',
  },
  {
    prop: "value",
    cat: "core",
    def: "undefined",
    type: "Date | string | { from, to }",
    desc: 'Single month: pass a Date object or ISO string — normalized internally to the 1st of that month. Range: pass { from: Date, to: Date }. Accepts "from"/"start" and "to"/"end" key aliases.',
  },
  {
    prop: "onChange",
    cat: "core",
    def: "() => {}",
    type: "(value) => void",
    desc: "Single month: fires with a Date object (1st of selected month). Range: fires with { from: Date, to: Date }. Fires null or { from: null, to: null } when cleared.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the month picker.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Select a month"',
    type: "string",
    desc: "Text shown in the trigger when no month is selected.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the trigger button.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius of the trigger button.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the trigger button. The month picker popup cannot be opened.",
  },
  {
    prop: "minDate",
    cat: "limit",
    def: "undefined",
    type: "Date",
    desc: "Earliest selectable month. Months before this are greyed out and unclickable. Normalized to the 1st of that month internally.",
  },
  {
    prop: "maxDate",
    cat: "limit",
    def: "undefined",
    type: "Date",
    desc: "Latest selectable month. Months after this are greyed out and unclickable. Normalized to the 1st of that month internally.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const PASSWORD_PROPS = [
  // Core
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: "password",
    desc: 'Must be "password" to render the password input with visibility toggle and password generator.',
  },
  {
    prop: "value",
    cat: "core",
    def: '""',
    type: "string",
    desc: "Controlled value of the password input. Always pass your state variable here.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(e: ChangeEvent) => void",
    desc: "Fires with a native-like event object { target: { value } }. Use e.target.value to read the new password value.",
  },
  // Display
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the password field.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Enter text..."',
    type: "string",
    desc: "Placeholder text shown inside the input when empty.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the input.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius of the input.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
  // Behavior
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the input, visibility toggle, and generate button. Shows gray background with cursor-not-allowed.",
  },
  {
    prop: "noGenerate",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Hides the password generator button (the ✦ icon). Use when you only want the visibility toggle without auto-generation.",
  },
  {
    prop: "showValidation",
    cat: "validation",
    def: "true",
    type: "boolean",
    desc: "Shows a validation popup when the input is focused. Lists all password requirements with pass/fail indicators. Only shown when noGenerate is false.",
  },
  // Generation
  {
    prop: "passwordFormat",
    cat: "generation",
    def: "{}",
    type: "object",
    desc: "Customizes password generation rules. See passwordFormat options below for full details.",
  },
  {
    prop: "customCharacterMapping",
    cat: "generation",
    def: "{}",
    type: "{ [char]: string }",
    desc: "Override specific character substitutions used during password generation. Merged with the default mapping. Example: { a: '@', b: '#' }.",
  },
];
export const PASSWORD_FORMAT_PROPS = [
  {
    prop: "base",
    cat: "generation",
    def: '""',
    type: "string",
    desc: "A base string used as the seed for generation. Characters are transformed using the character mapping and random case changes. The generated password will be derived from this string.",
  },
  {
    prop: "minLength",
    cat: "generation",
    def: "8",
    type: "number",
    desc: "Minimum length of the generated password.",
  },
  {
    prop: "maxLength",
    cat: "generation",
    def: "20",
    type: "number",
    desc: "Maximum length of the generated password.",
  },
  {
    prop: "hasUpperCase",
    cat: "validation",
    def: "true",
    type: "boolean",
    desc: "Generated password must include at least one uppercase letter.",
  },
  {
    prop: "hasLowerCase",
    cat: "validation",
    def: "true",
    type: "boolean",
    desc: "Generated password must include at least one lowercase letter.",
  },
  {
    prop: "hasNumber",
    cat: "validation",
    def: "true",
    type: "boolean",
    desc: "Generated password must include at least one numeric digit.",
  },
  {
    prop: "hasSpecialChar",
    cat: "validation",
    def: "true",
    type: "boolean",
    desc: "Generated password must include at least one special character from allowedSpecialChars.",
  },
  {
    prop: "allowedSpecialChars",
    cat: "validation",
    def: '"&()#*$@"',
    type: "string",
    desc: "String of allowed special characters for generation and validation. Only characters in this string are permitted.",
  },
];
export const MAILIDLIST_PROPS = [
  // Core
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: "toMailIds",
    desc: 'Must be "toMailIds" to render the email chip input.',
  },
  {
    prop: "value",
    cat: "core",
    def: "[]",
    type: "string[]",
    desc: "Controlled array of currently added email addresses. Each item is a plain email string.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(emails: string[]) => void",
    desc: "Fires with the updated email array whenever a chip is added or removed. Receives the array directly — not a native event.",
  },
  // Display
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the field.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Enter text..."',
    type: "string",
    desc: "Placeholder text shown inside the input when no emails are added yet. Automatically changes to 'Maximum N emails allowed' when maxCount is reached.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"sm" | "md" | "lg"',
    desc: "Controls padding and font size of the input field.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius of the input field.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
  // Behavior
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the entire field. No emails can be added or removed. Existing chips are shown in read-only style.",
  },
  {
    prop: "maxCount",
    cat: "behavior",
    def: "5",
    type: "number",
    desc: "Maximum number of email chips allowed. Input is automatically disabled once this limit is reached.",
  },
  // Suggestion
  {
    prop: "userList",
    cat: "suggestion",
    def: "[{ id, name, email }]",
    type: "{ id, name, email, avatar? }[]",
    desc: "List of users shown as suggestions when the user types. Filters by name or email. Already-added emails are excluded automatically. Each user shows an Avatar, name, and email in the dropdown.",
  },
];
export const TEXTAREALIST_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: "textArea",
    desc: 'Must be "textArea" to render the textarea field.',
  },
  {
    prop: "value",
    cat: "core",
    def: '""',
    type: "string",
    desc: "Controlled value of the textarea. Always pass your state variable here.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(value: string) => void",
    desc: "Fires with the new string value on every keystroke. Receives the value directly — not a native event. Typing beyond maxLength shows a toast error and the change is blocked.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the textarea.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Enter text..."',
    type: "string",
    desc: "Placeholder text shown inside the textarea when empty.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the textarea.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius of the textarea.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "rows",
    cat: "display",
    def: "2",
    type: "number",
    desc: "Number of visible text rows. Controls the initial height of the textarea. Note: FormFieldSet default is 2, TextArea component default is 4.",
  },
  {
    prop: "maxLength",
    cat: "behavior",
    def: "300",
    type: "number",
    desc: "Maximum number of characters allowed. A character counter is shown in the top-right of the label area. Typing beyond this limit shows a toast error and blocks the change.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the textarea. Shows gray background with cursor-not-allowed. Input changes are blocked.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const PHONE_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"phone"',
    desc: 'Use "phone" for a single phone number input with country code selector.',
  },
  {
    prop: "value",
    cat: "core",
    def: '""',
    type: "string",
    desc: "Controlled phone number value (digits only, without country code). Pass your state variable here.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(fullValue, details) => void",
    desc: "Fires with two arguments: (1) fullValue — the complete phone string including country code, (2) details — { countryCode, number, fullNumber, flag, error }. Use details.number to get just the digits.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the phone input.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Enter phone number..."',
    type: "string",
    desc: "Placeholder text shown inside the number input.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the input.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius of the input.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the input and country selector. No changes can be made.",
  },
  {
    prop: "code",
    cat: "behavior",
    def: '"+1"',
    type: "string",
    desc: 'Initial country calling code shown in the selector (e.g. "+91"). Passed as selectedCountryCode to the underlying component. Updates when the user selects a different country.',
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const PHONE_NEW_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"phoneNew"',
    desc: 'Use "phoneNew" for a multi-phone input that stores an array of { code, number } objects.',
  },
  {
    prop: "values",
    cat: "core",
    def: "[]",
    type: "{ code: string, number: string }[]",
    desc: 'Controlled array of phone objects. Note: uses "values" (plural) — not "value". Each item has a code (country calling code) and number (digits only).',
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(phones: { code, number }[]) => void",
    desc: "Fires with the updated phone array whenever a number is added, edited, or removed. Receives the array directly.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the phone input.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Enter phone number..."',
    type: "string",
    desc: "Placeholder shown in the number input when no phones are added.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the input.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"rounded"',
    desc: "Controls the border radius of the input.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the input and country selector. Existing phones are shown in read-only style.",
  },
  {
    prop: "multiple",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Allows adding multiple phone numbers. Press Enter after entering a number to add another. Shows a phone count badge and a list button to manage all added numbers.",
  },
  {
    prop: "code",
    cat: "behavior",
    def: '"+1"',
    type: "string",
    desc: "Initial country calling code for the selector. Passed as selectedCountryCode to the underlying component.",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const EMAIL_PROPS = [
  // Core
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: "email",
    desc: 'Must be "email" to render the email input.',
  },
  {
    prop: "value",
    cat: "core",
    def: "[]",
    type: "string[] | string",
    desc: "When multiple is true: controlled array of email strings. When multiple is false: controlled single email string. Always pass your state variable here.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(emails: string[] | string) => void",
    desc: "When multiple is true: fires with the updated email array. When multiple is false: fires with the raw input string (not a native event). Pass your state setter directly for multiple mode.",
  },
  // Display
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the field.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Enter email"',
    type: "string",
    desc: 'Placeholder text shown in the input. Automatically changes to "Add more" once at least one email is added.',
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the input.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"rounded"',
    type: '"none" | "sm" | "md" | "lg" | "xl" | "full" | "rounded"',
    desc: "Controls the border radius. Only applies in single (non-multiple) mode.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
  // Behavior
  {
    prop: "multiple",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Enables multi-email mode. Emails are added by pressing Enter or pasting. Shows a count badge and a list button to manage all added emails. Duplicate and invalid emails are rejected with a toast.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the input. Existing emails are shown in read-only style. No emails can be added or removed.",
  },
  {
    prop: "maxLength",
    cat: "behavior",
    def: "20",
    type: "number",
    desc: "Maximum character length for individual email input. Note: this limits input length, not the number of emails.",
  },
];
export const TOGGLE_NEW_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"toggleNew"',
    desc: 'Must be "toggleNew" to render the animated pill-style toggle.',
  },
  {
    prop: "value",
    cat: "core",
    def: "true",
    type: "boolean",
    desc: "Controlled on/off state. true = On (green), false = Off (red).",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "() => {}",
    type: "(value: boolean) => void",
    desc: "Fires with the new boolean value when the toggle is clicked. Pass your state setter directly.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the toggle.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"sm"',
    type: '"xs" | "xs-sm" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls the width and height of the toggle pill. Larger sizes also increase the knob and label text.",
  },
  {
    prop: "options",
    cat: "display",
    def: '["On","Off"]',
    type: "string[]",
    desc: 'Two-item array for the on and off labels shown inside the pill. Currently displays "On"/"Off" regardless of this value — reserved for future use.',
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the toggle. Clicking has no effect.",
  },
];
export const TOGGLE_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"toggle"',
    desc: 'Must be "toggle" to render the classic slide toggle (checkbox style).',
  },
  {
    prop: "isChecked",
    cat: "core",
    def: "false",
    type: "boolean",
    desc: 'Controlled checked state. Note: uses "isChecked" — not "value" — for this toggle type.',
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(value: boolean) => void",
    desc: "Fires with the new boolean value (!isChecked) when toggled. Pass your state setter directly.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the toggle.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "label",
    cat: "display",
    def: '""',
    type: "string",
    desc: "Text shown next to the toggle knob itself (inline label, separate from title).",
  },
  {
    prop: "labelPosition",
    cat: "display",
    def: '"before"',
    type: '"before" | "after"',
    desc: "Controls whether the inline label appears before or after the toggle knob.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"sm"',
    type: '"xs" | "sm" | "md" | "lg"',
    desc: "Controls the width and height of the toggle track and knob.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the toggle. Shows 50% opacity and cursor-not-allowed.",
  },
];
export const TOGGLE_INPUT_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"toggleInput"',
    desc: 'Must be "toggleInput" to render the two-segment sliding toggle.',
  },
  {
    prop: "value",
    cat: "core",
    def: "true",
    type: "boolean",
    desc: "Controlled state. true = left segment active, false = right segment active.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(value: boolean) => void",
    desc: "Fires with the new boolean value when either segment is clicked.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the toggle.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "leftText",
    cat: "display",
    def: "undefined",
    type: "string",
    desc: "Text label shown inside the left segment.",
  },
  {
    prop: "rightText",
    cat: "display",
    def: "undefined",
    type: "string",
    desc: "Text label shown inside the right segment.",
  },
  {
    prop: "leftIcon",
    cat: "display",
    def: "null",
    type: "string",
    desc: "Material Symbols icon name shown in the left segment. Falls back to the icon prop if not provided.",
  },
  {
    prop: "rightIcon",
    cat: "display",
    def: "null",
    type: "string",
    desc: "Material Symbols icon name shown in the right segment. Falls back to the icon prop if not provided.",
  },
  {
    prop: "icon",
    cat: "display",
    def: '"check_circle"',
    type: "string",
    desc: "Fallback Material Symbols icon used for both segments when leftIcon or rightIcon are not provided.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"sm" | "md" | "lg" | "xl"',
    desc: "Controls the height and font size of the toggle segments.",
  },
  {
    prop: "rounded",
    cat: "display",
    def: '"md"',
    type: '"none" | "sm" | "md" | "lg" | "full"',
    desc: "Controls the border radius of the outer container.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables clicking. Shows 60% opacity and cursor-not-allowed.",
  },
  {
    prop: "colors",
    cat: "display",
    def: "{ active: 'bg-primary', ... }",
    type: "object",
    desc: "Customize segment colors. Keys: active, inactive, activeText, inactiveText, border. Each takes a Tailwind class string.",
  },
];
export const FILE_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"file"',
    desc: 'Must be "file" to render the styled file input with a Choose File button.',
  },
  {
    prop: "value",
    cat: "core",
    def: "[]",
    type: "File[]",
    desc: "Controlled array of File objects. Pass your state variable here. Reset to an empty array to clear selected files.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(files: File[]) => void",
    desc: "Fires with the updated File array whenever files are selected or removed. Receives the array directly — pass your state setter.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the file input.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Choose file"',
    type: "string",
    desc: "Text shown on the Choose File button inside the input.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls padding and font size of the file input.",
  },
  {
    prop: "shadow",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Replaces the visible border with a subtle box shadow (inputShadow class).",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the file input. Clicking has no effect. Shows gray background.",
  },
  {
    prop: "multiple",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Allows selecting multiple files. Adds a file count badge and a list button to manage selected files in a modal. Duplicate files (same name + size + type) are automatically skipped.",
  },
  {
    prop: "accept",
    cat: "behavior",
    def: "undefined",
    type: "string",
    desc: 'Restricts which file types can be selected. Pass a comma-separated MIME type or extension string. Example: ".pdf,.docx" or "image/*".',
  },
  {
    prop: "error",
    cat: "display",
    def: "false",
    type: "string | false",
    desc: "Error message shown in red below the field.",
  },
  {
    prop: "required",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Shows a red asterisk (*) next to the label.",
  },
];
export const FILE_BUTTON_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"fileButton"',
    desc: 'Must be "fileButton" to render a simple Upload button that opens the file picker on click.',
  },
  {
    prop: "value",
    cat: "core",
    def: "undefined",
    type: "File[]",
    desc: "Controlled array of File objects. Pass your state variable here.",
  },
  {
    prop: "onChange",
    cat: "core",
    def: "undefined",
    type: "(files: File[]) => void",
    desc: "Fires with the selected File array when the user picks files. Receives the array directly.",
  },
  {
    prop: "title",
    cat: "display",
    def: '"Title"',
    type: "string",
    desc: "Label shown above the button (FormFieldSet label, not the button text). The button text is set via the same title prop passed to FormFieldSet — or defaults to 'Upload'.",
  },
  {
    prop: "placeholder",
    cat: "display",
    def: '"Choose file"',
    type: "string",
    desc: "Not displayed visually — passed through but unused in this type. Use title for button text.",
  },
  {
    prop: "size",
    cat: "display",
    def: "undefined",
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Size of the Upload button.",
  },
  {
    prop: "color",
    cat: "display",
    def: "undefined",
    type: "string",
    desc: "Color of the Upload button. Accepts any color name supported by the Button component.",
  },
  {
    prop: "disabled",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Disables the Upload button. The file picker cannot be opened.",
  },
  {
    prop: "multiple",
    cat: "behavior",
    def: "false",
    type: "boolean",
    desc: "Allows selecting multiple files at once.",
  },
  {
    prop: "accept",
    cat: "behavior",
    def: "undefined",
    type: "string",
    desc: 'Restricts file types. Pass a comma-separated extension or MIME string. Example: ".xls,.xlsx" or "image/*".',
  },
];
export const FILE_UPLOAD_PROPS = [
  {
    prop: "type",
    cat: "core",
    def: '"text"',
    type: '"fileupload"',
    desc: 'Must be "fileupload" to render the drag-and-drop PDF card uploader.',
  },
  {
    prop: "files",
    cat: "core",
    def: "[]",
    type: "{ id, name, pages, preview, type, data }[]",
    desc: "Controlled array of uploaded file objects. Pass your state variable here. Reset to [] to clear all cards.",
  },
  {
    prop: "onFileSelect",
    cat: "core",
    def: "undefined",
    type: "(files: FileObject[]) => void",
    desc: "Fires with the updated file array whenever a file is selected or dropped. Receives the new array directly — pass your state setter.",
  },
  {
    prop: "noTitle",
    cat: "display",
    def: "false",
    type: "boolean",
    desc: "Hides the label above the field. Set to true in the demo since this component fills a container rather than sitting in a form.",
  },
  {
    prop: "size",
    cat: "display",
    def: '"md"',
    type: '"xs" | "sm" | "md" | "lg" | "xl"',
    desc: "Controls the height and font size of the empty drop zone area before any files are uploaded.",
  },
  {
    prop: "cardHeight",
    cat: "display",
    def: '"h-64"',
    type: "string (Tailwind h-* class)",
    desc: "Tailwind height class applied to each uploaded file card. Example: h-100, h-64, h-48.",
  },
  {
    prop: "cardWidth",
    cat: "display",
    def: '"w-48"',
    type: "string (Tailwind w-* class)",
    desc: "Tailwind width class applied to each uploaded file card. Example: w-80, w-48.",
  },
];
